import { CsiJournalPapers } from "@/components/CsiJournalPapers";
import { CiteButtons } from "@/components/CiteButtons";
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { mergeOrcid } from "@/lib/orcid";
import { useSeo } from "@/hooks/useSeo";
import { Link } from "react-router-dom";
import { ExternalLink, FileText, BookOpen, FlaskConical, Shield, Scale, Users, Lock, Database, AlertTriangle, ScrollText, ArrowRight, Search } from "lucide-react";

const STANDARDS = [
  {
    code: "CSF-STRAT",
    tag: "Strategic Framework",
    year: "2026",
    title: "Converged Security Framework - Strategic Standards for Unified Risk Management and Organisational Resilience",
    summary: "Strategic framework for unified risk management and organisational resilience, integrating cybersecurity and physical security into a cohesive operational strategy.",
    meta: "Version 2.0 · Issued and effective 13 October 2026 · Published by GCSB",
    url: "https://gcsb.uk/standards/csf-strategic-standards-resilience",
    icon: Shield,
  },
  {
    code: "ST-CSF.001",
    tag: "Main Standard",
    year: "2026",
    title: "ST-CSF.001 - Converged Security Framework Main Standard",
    summary: "Foundational standard defining best practices for implementing unified risk management through converged security frameworks across cyber, physical and OT domains.",
    meta: "Version 2.0 · Issued and effective 13 October 2026 · Published by GCSB",
    url: "https://gcsb.uk/standards/csf-converged-security-framework",
    icon: Shield,
  },
  {
    code: "ST-CSF.TRA.001",
    tag: "Training Standard",
    year: "2026",
    title: "ST-CSF.TRA.001 - Training and Awareness",
    summary: "Framework for developing unified security competencies across cyber-physical domains, defining cross-functional training programmes, awareness coverage targets and tabletop exercises validating cross-domain incident response.",
    meta: "Version 2.0 · Issued and effective 13 October 2026 · Published by GCSB",
    url: "https://gcsb.uk/standards/csf-tra-001-training",
    icon: Users,
  },
  {
    code: "ST-CSF.GLA.001",
    tag: "Governance Standard",
    year: "2026",
    title: "ST-CSF.GLA.001 - Governance and Leadership Framework",
    summary: "Mandatory requirements for governance and leadership structures that integrate cybersecurity, physical security and OT security under unified management.",
    meta: "Version 2.0 · Issued and effective 13 October 2026 · Published by GCSB",
    url: "https://gcsb.uk/standards/csf-gla-001-governance",
    icon: Scale,
  },
  {
    code: "ST-CSF.TIA.001",
    tag: "Technology Standard",
    year: "2026",
    title: "ST-CSF.TIA.001 - Technology Integration and Architecture",
    summary: "Best practices for unified technology integration and architecture across cyber-physical security domains - integrated SIEM/PSIM, Zero Trust across IT and OT, and AI/ML for predictive threat analysis.",
    meta: "Version 2.0 · Issued and effective 13 October 2026 · Published by GCSB",
    url: "https://gcsb.uk/standards/csf-tia-001-technology",
    icon: Database,
  },
  {
    code: "ST-CSF.IRBC.001",
    tag: "Incident Response Standard",
    year: "2026",
    title: "ST-CSF.IRBC.001 - Incident Response and Business Continuity Framework",
    summary: "Unified incident response and business continuity across cyber-physical security domains, addressing hybrid, systemic and cascading risks with coordinated response teams and integrated BCP.",
    meta: "Version 2.0 · Issued and effective 13 October 2026 · Published by GCSB",
    url: "https://gcsb.uk/standards/csf-irbc-001-incident-response",
    icon: AlertTriangle,
  },
  {
    code: "ST-CSF.IAM.001",
    tag: "Identity Standard",
    year: "2026",
    title: "ST-CSF.IAM.001 - Identity and Access Management Standard",
    summary: "Requirements for unified IAM across cybersecurity, physical security and OT - unified identity governance, lifecycle management, authentication, Privileged Access Management and federation.",
    meta: "Version 2.0 · Issued and effective 13 October 2026 · Published by GCSB",
    url: "https://gcsb.uk/standards/csf-iam-001-identity-access",
    icon: Lock,
  },
  {
    code: "ST-CSF.DPP.001",
    tag: "Data Protection Standard",
    year: "2026",
    title: "ST-CSF.DPP.001 - Data Protection and Privacy Standard for the Converged Security Framework",
    summary: "Best practices for unified data protection and privacy across cyber-physical domains - integrated PIA, Data Governance, Privacy by Design Architecture, DLP, data subject rights and breach notification.",
    meta: "Version 2.0 · Issued and effective 13 October 2026 · Published by GCSB",
    url: "https://gcsb.uk/standards/csf-dpp-001-data-protection",
    icon: Database,
  },
  {
    code: "ST-CSF.RMA.001",
    tag: "Risk Management Standard",
    year: "2026",
    title: "ST-CSF.RMA.001 - Converged Security Risk Management and Assessment Standard",
    summary: "Converged security risk management protocols, strategic assessment methodologies and unified governance frameworks - unified risk assessment, threat analysis, integrated compliance management and CCSO authority structures.",
    meta: "Version 2.0 · Issued and effective 13 October 2026 · Published by GCSB",
    url: "https://gcsb.uk/standards/csf-rma-001-risk-management",
    icon: Shield,
  },
  {
    code: "CCSO Policy",
    tag: "Domain Policy",
    year: "2026",
    title: "Chief Converged Security Officer (CCSO) Policy, Organisational and Leadership Standard",
    summary: "Unified governance framework for the CCSO role with executive authority across cyber, physical and OT domains - governance structure, risk management, IAM, incident response and compliance.",
    meta: "Version 2.0 · Issued and effective 13 October 2026 · Published by GCSB",
    url: "https://gcsb.uk/standards/ccso-policy",
    icon: ScrollText,
  },
];

const ORCID_URL = "https://orcid.org/0009-0006-6209-629X";

const STATIC_RESEARCH = [
  {
    tag: "Peer-Reviewed Paper",
    year: "2026",
    title: "A Unified Data Protection and Privacy Architecture for Cyber-Physical Systems: Managing Cross-Domain, Systemic, and Cascading Privacy Risks in Converged Environments",
    summary: "Introduces a Unified Data Protection and Privacy Architecture for cyber-physical ecosystems where IT, OT and physical security infrastructures share personal data flows. Four interdependent layers - Privacy Governance, Operational Privacy, Technology and Cryptographic Controls, and Assurance and Compliance - address cross-domain data flows, coordinated privacy incident response and regulatory compliance. Aligned with GDPR, NIS2, DORA, ISO/IEC 27001, ISO/IEC 27701, ISO 31000 and ISO 22301, with sector implications for critical infrastructure, financial services, industry and the public sector.",
    meta: "Author: Prof. Dr. Vladimir Bunic · Global Journal of Engineering and Technology Advances · Vol. 29, No. 1 (2026) · pp. 196–204 · October 2026 · DOI: 10.30574/gjeta.2026.29.1.0285",
    url: "https://doi.org/10.30574/gjeta.2026.29.1.0285",
    cta: "View in journal",
  },
  {
    tag: "Peer-Reviewed Paper",
    year: "2026",
    title: "Governing Cross-Domain Risk in AI-Driven Critical Infrastructure",
    summary: "Analyses the converged risk phenomenon in critical infrastructure where AI, digital twins, IoT ecosystems and autonomous OT now take part in operational decisions. A qualitative multi-scenario reconstruction traces propagation pathways such as telemetry corruption, digital twin drift, autonomy escalation, AI misclassification, authentication instability and multi-sector cascading failures, and presents a single governance framework for SOC and GSOC built on telemetry validation, autonomy monitoring, prediction model alignment and systemic risk monitoring - supported by the Converged Security Framework.",
    meta: "Author: Prof. Dr. Vladimir Bunic · International Journal of Science and Research Archive · Vol. 21, No. 1 (2026) · pp. 296–302 · October 2026 · DOI: 10.30574/ijsra.2026.21.1.1877",
    url: "https://doi.org/10.30574/ijsra.2026.21.1.1877",
    cta: "View in journal",
  },
  {
    tag: "Peer-Reviewed Paper",
    year: "2026",
    title: "A Unified Cyber-Physical Incident Response and Business Continuity Architecture for Managing Hybrid, Systemic, and Cascading Risks Across IT, OT, and Physical Security Domains",
    summary: "Proposes a unified IRBC architecture that integrates cross-domain response policies, Zero Trust enforcement, AI-driven analytics and recovery plans across cyber-physical systems. Draws on ISO/IEC 27001:2022, ISO 22301:2019, ISO 31000:2018, NIST SP 800-61, NIST SP 800-34, NFPA 1600 and European standards, with a dependency model mapping how hybrid, systemic and cascading risks propagate across IT, OT and physical domains - relevant to critical infrastructure, financial institutions, manufacturing and public-sector operators.",
    meta: "Author: Prof. Dr. Vladimir Bunic · International Journal of Science and Research Archive · Vol. 20, No. 03 (2026) · pp. 032–043 · September 2026 · DOI: 10.30574/ijsra.2026.20.3.1674",
    url: "https://doi.org/10.30574/ijsra.2026.20.3.1674",
    cta: "View in journal",
  },
  {
    tag: "Peer-Reviewed Paper",
    year: "2026",
    title: "Risk Convergence in Critical Infrastructure Governance in the European Union: A Qualitative Framework for Integrated Multi-Domain Risk Oversight",
    summary: "Qualitative conceptual study analysing the causes and effects of risk convergence across EU critical infrastructure. Identifies five patterns of risk convergence (cyber-physical interdependencies, operational-cyber propagation, human-factor amplification, regulatory-risk misalignment, organisational fragmentation) and proposes a hybrid governance system with structural, operational and assurance layers - one of the earliest governance-focused models tailored to converged risk environments in EU critical infrastructure. Triangulated with NIS2, CER, GDPR, DORA and the Cyber Resilience Act.",
    meta: "Author: Prof. Dr. Vladimir Bunic · International Journal of Public Administration, Management and Economic Development · Vol. 11, No. 1 (2026) · pp. 34–46 · June 2026 · DOI: 10.60026/ijpamed.v11i1.261",
    url: "https://doi.org/10.60026/ijpamed.v11i1.261",
    cta: "View in journal",
  },
];

const STATIC_BOOKS = [
  {
    tag: "Book",
    year: "2025",
    title: "Digital Strategies for Enterprise Converged Security Systems",
    summary: "How digital strategies can boost integration and overall performance of enterprise converged security systems. Mixed-methods research combining qualitative insights and numerical data on everyday security practices, technology uptake and stakeholder perspectives - with particular emphasis on healthcare environments where patient data and operational continuity are critical.",
    meta: "Author: Prof. Dr. Vladimir Bunic · Lambert Academic Publishing · October 2025",
    url: "https://flipbooks.omniscriptum.com/DIGITAL_STRATEGIES_FOR_ENTERPRISE_CONVERGED_SECURITY_SYSTEMS.html",
    cta: "View publication",
  },
  {
    tag: "Book",
    year: "2025",
    title: "Challenges in the Governance of Converged Security Systems in Enterprises within the European Union (EU)",
    summary: "Investigates complexities and obstacles faced by enterprises in the EU when implementing governance frameworks for converged security systems. Explores regulatory discrepancies, technological integration issues and organisational-culture impacts on effective governance. Qualitative data from case studies, interviews and surveys with security professionals and compliance officers.",
    meta: "Author: Prof. Dr. Vladimir Bunic · LAP Lambert Academic Publishing · March 2025 · ISBN 978-6208426330",
    url: "https://flipbooks.omniscriptum.com/Governance_Challenges_of_Converged_Security_Systems_in_Enterprises.html#page/15",
    cta: "View publication",
  },
  {
    tag: "Book",
    year: "2024",
    title: "EU Legislative Frameworks of Converged Security Systems",
    summary: "Examines how laws apply to enterprise security systems within the EU. Highlights fragmentation across jurisdictions, gaps in compliance and the impact on risk management - especially in healthcare, where data protection is critical. Argues for more harmonised legislation to support stronger operational resilience across member states.",
    meta: "Author: Prof. Dr. Vladimir Bunic · Lambert Academic Publishing · November 2024",
    url: "https://flipbooks.omniscriptum.com/EU_Legislative_Frameworks_of_Converged_Security_Systems.html",
    cta: "View publication",
  },
  {
    tag: "Book",
    year: "2023",
    title: "Converged Security for Manufacturer's Security: Guidelines in Standardization of Converged Security Manufacturing Organizations",
    summary: "A practitioner-oriented book covering converged security systems and their function in manufacturing environments. Detailed technology solutions, implementation guidance across varied manufacturing settings and operational best practices for converged security operations.",
    meta: "Author: Prof. Dr. Vladimir Bunic · Independently published · August 2023 · ISBN 979-8858200857",
    url: "https://a.co/d/evI9hYA",
    cta: "View on Amazon",
  },
];

const tabMeta = (research, books) => ({
  all: { label: "All publications", count: STANDARDS.length + research.length + books.length, icon: FileText },
  standards: { label: "GCSB Standards", count: STANDARDS.length, icon: Shield },
  research: { label: "Research Papers", count: research.length, icon: FlaskConical },
  books: { label: "Books", count: books.length, icon: BookOpen },
});

function PubCard({ item, external = true, citable = false }) {
  const Icon = item.icon || FileText;
  const isLocal = item.url && item.url.startsWith("/");
  return (
    <article className="card-warm p-6 flex flex-col gap-3" data-testid={`pub-${item.code ? item.code.replace(/\s|\./g, '-').toLowerCase() : item.title.slice(0, 24).replace(/\s/g, '-').toLowerCase()}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-8 h-8 rounded-lg bg-[var(--muted-surface)] grid place-items-center text-[var(--gold-deep)] shrink-0">
            <Icon size={15}/>
          </div>
          <span className="inline-flex items-center rounded-full bg-[var(--muted-surface)] border border-[var(--border-warm)] px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase text-[var(--navy)]">
            {item.tag}
          </span>
          {item.code && item.url?.includes("gcsb.uk") && (
            <span className="inline-flex items-center rounded-md bg-[var(--navy)] text-[var(--gold-soft)] px-2 py-0.5 text-[10px] font-mono font-bold tracking-wide" data-testid={`pub-code-${item.code.replace(/\s|\./g, '-').toLowerCase()}`}>
              {item.code}
            </span>
          )}
        </div>
        {item.year && (
          <span className="text-[10px] font-bold tracking-widest uppercase text-[var(--gold-deep)]">{item.year}</span>
        )}
      </div>
      <h3 className="font-display text-xl leading-tight">{item.title}</h3>
      <p className="text-sm text-zinc-700 leading-relaxed flex-1">{item.summary}</p>
      <div className="text-[11px] text-zinc-500 italic">{item.meta}</div>
      {citable && <CiteButtons item={item} testid={item.title.slice(0, 20).replace(/\s/g, '-').toLowerCase()}/>}
      <a href={item.url} target="_blank" rel={isLocal ? "noopener" : "noreferrer"}
         className="btn-terracotta inline-flex items-center gap-1.5 self-start px-4 py-2 rounded-full text-xs font-semibold mt-1"
         data-testid={`pub-link-${item.title.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}>
        {item.cta || (isLocal ? "Read standard" : (item.url?.includes("gcsb.uk") ? "Read on GCSB" : "Read document"))} {isLocal ? <FileText size={12}/> : <ExternalLink size={12}/>}
      </a>
    </article>
  );
}

export default function PublicationsPage() {
  useSeo({ title: "Publications & Research", description: "Research and publications from the Converged Security Institute on converged security governance and leadership.", path: "/publications" });
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [orcid, setOrcid] = useState({ works: [], synced_at: null });
  useEffect(() => { api.get("/orcid/works").then(({ data }) => setOrcid(data)).catch(() => {}); }, []);
  const { research: RESEARCH, books: BOOKS } = useMemo(() => mergeOrcid(orcid.works, STATIC_RESEARCH, STATIC_BOOKS), [orcid.works]);
  const TAB_META = tabMeta(RESEARCH, BOOKS);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const inScope = {
      standards: tab === "all" || tab === "standards" ? STANDARDS : [],
      research: tab === "all" || tab === "research" ? RESEARCH : [],
      books: tab === "all" || tab === "books" ? BOOKS : [],
    };
    if (!q) return inScope;
    const match = (it) =>
      it.title.toLowerCase().includes(q) ||
      it.summary.toLowerCase().includes(q) ||
      (it.code && it.code.toLowerCase().includes(q)) ||
      (it.tag && it.tag.toLowerCase().includes(q));
    return {
      standards: inScope.standards.filter(match),
      research: inScope.research.filter(match),
      books: inScope.books.filter(match),
    };
  }, [tab, query, RESEARCH, BOOKS]);

  const totalShown = filtered.standards.length + filtered.research.length + filtered.books.length;

  return (
    <div data-testid="publications-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6">
        <div className="eyebrow mb-3">Research & Scholarship</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="publications-h1">
          Converged Security <span className="italic text-brand-gold">Publications & CSF Standards</span>
        </h1>
        <p className="mt-4 text-sm sm:text-base text-zinc-700 max-w-3xl leading-relaxed">
          Research papers, policy briefs, frameworks and articles advancing converged security - including the full GCSB
          Converged Security Framework (CSF) suite of published standards and policies (Version 2.0).
        </p>
      </section>

      {/* Tabs + search */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
        <div className="flex gap-2 flex-wrap">
          {Object.entries(TAB_META).map(([key, meta]) => {
            const Icon = meta.icon;
            const active = tab === key;
            return (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${active
                  ? "bg-[var(--navy)] text-[var(--gold-soft)] border-[var(--navy)]"
                  : "bg-white text-[var(--navy)] border-[var(--border-warm)] hover:bg-[var(--muted-surface)]"}`}
                data-testid={`pub-tab-${key}`}
              >
                <Icon size={13}/> {meta.label} <span className="opacity-70">· {meta.count}</span>
              </button>
            );
          })}
        </div>
        <div className="relative md:ml-auto md:w-80">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <input
            type="search"
            placeholder="Search publications…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ paddingLeft: "2.5rem" }}
            className="w-full rounded-full border border-[var(--border-warm)] bg-white pl-9 pr-4 py-2 text-sm outline-none focus:border-[var(--navy)]"
            data-testid="publications-search"
          />
        </div>
      </section>

      {/* Standards */}
      {filtered.standards.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="eyebrow mb-1">GCSB CSF Standards & Policies · {filtered.standards.length} / {STANDARDS.length}</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-3">GCSB CSF Standards & Policies</h2>
          <p className="text-sm text-zinc-700 max-w-3xl mb-6">
            The Converged Security Framework suite, Version 2.0, issued and effective 13 October 2026 by the Global Converged Security Board (GCSB).
            Each document is published and maintained by GCSB - read the current versions online at <a href="https://gcsb.uk/standards" target="_blank" rel="noreferrer" className="text-[var(--terracotta)] underline">gcsb.uk/standards</a>.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {filtered.standards.map((s) => <PubCard key={s.code} item={s}/>)}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10" data-testid="gcsb-journal-note">
        <div className="card-warm p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div className="max-w-3xl">
            <div className="eyebrow mb-1">Call for papers</div>
            <h3 className="font-display text-2xl">GCSB Journal of Converged Security</h3>
            <p className="text-sm text-zinc-700 mt-1">CSI faculty and graduates are invited to submit papers to the GCSB Journal of Converged Security. There are no publication fees, and every submission receives double-blind peer review.</p>
          </div>
          <a href="https://gcsb.uk/journal" target="_blank" rel="noopener noreferrer" className="btn-terracotta px-5 py-3 rounded-full text-sm font-semibold shrink-0" data-testid="gcsb-journal-link">Submit a paper ↗</a>
        </div>
        <CsiJournalPapers/>
      </section>

      {/* Research */}
      {filtered.research.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="eyebrow mb-1">Peer-reviewed research · {filtered.research.length} / {RESEARCH.length}</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-3">Research Papers</h2>
          <p className="text-sm text-zinc-700 max-w-3xl mb-6">
            Peer-reviewed journal articles by Prof. Dr. Vladimir Bunic, as listed on his <a href={ORCID_URL} target="_blank" rel="noreferrer" className="text-[var(--terracotta)] underline" data-testid="publications-orcid-link">ORCID profile (0009-0006-6209-629X)</a>. New works added to ORCID appear here automatically.{orcid.synced_at && <span className="block text-xs text-zinc-500 mt-1" data-testid="publications-orcid-synced">Synced with ORCID {new Date(orcid.synced_at).toLocaleString()}</span>}
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {filtered.research.map((r) => <PubCard key={r.title} item={r} citable/>)}
          </div>
        </section>
      )}

      {/* Books */}
      {filtered.books.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="eyebrow mb-1">Books · {filtered.books.length} / {BOOKS.length}</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-3">Published Books</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {filtered.books.map((b) => <PubCard key={b.title} item={b} citable/>)}
          </div>
        </section>
      )}

      {totalShown === 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-sm text-zinc-500">No publications match "{query}".</div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
        <div className="rounded-3xl bg-brand-navy text-white p-8 sm:p-12">
          <div className="grid md:grid-cols-5 gap-6 items-center">
            <div className="md:col-span-3">
              <div className="eyebrow text-[var(--gold-soft)]">Collaborate or cite CSI research</div>
              <h3 className="font-display text-3xl sm:text-4xl mt-2">Work with the Institute</h3>
              <p className="text-zinc-300 mt-3 text-sm sm:text-base max-w-2xl">
                Interested in joint research, policy consultation, speaking engagements or implementing the GCSB
                Converged Security Framework in your organisation? Reach out to our faculty.
              </p>
            </div>
            <div className="md:col-span-2 flex md:justify-end flex-wrap gap-3">
              <a href="mailto:info@convergedsecurity.es?subject=CSI%20Research%20Enquiry"
                 className="btn-terracotta px-5 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2"
                 data-testid="publications-contact-btn">
                Contact CSI
              </a>
              <Link to="/instructors"
                    className="chip px-5 py-3 bg-white/5 border-white/20 text-white hover:bg-white/10 inline-flex items-center gap-2"
                    data-testid="publications-faculty-btn">
                Meet the faculty <ArrowRight size={14}/>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
